import React, { useState, useEffect } from 'react';
import { useBodyScrollLock } from '../utils/scrollLock';
import {
  Anime,
  AnimeEpisode,
  SEASON_CONFIGS,
  TIER_CONFIGS,
} from '../types/anime';
import {
  audioTypeFromSources,
  fetchWakuDetail,
  isDetailForAnime,
  mergeEpisodeLists,
  parseWakuIsoToDayTime,
} from '../services/animeService';
import {
  detectPlatformFromUrl,
  episodeHasSource,
  formatImageUrl,
  getDayConfig,
  getPlatformBadgeStyle,
  getSourceAudio,
} from '../utils/helpers';
import { MalScoreBadge } from './MalScoreBadge';
import { MalRatingPicker } from './MalRatingPicker';
import { AnimeDownloadModal } from './AnimeDownloadModal';
import { OfflineEpisodeList } from './OfflineEpisodeList';
import {
  X,
  ExternalLink,
  Play,
  Check,
  Bell,
  BellOff,
  Loader2,
  Pencil,
  Send,
  Sparkles,
  Calendar,
  Clock,
  Download,
  Star,
} from 'lucide-react';

interface AnimeDetailModalProps {
  anime: Anime | null;
  onClose: () => void;
  onEdit: (anime: Anime) => void;
  onUpdateAnime: (updated: Anime) => void;
  onSendSingleLineNotify: (anime: Anime) => void;
  isSendingLine: boolean;
}

export const AnimeDetailModal: React.FC<AnimeDetailModalProps> = ({
  anime,
  onClose,
  onEdit,
  onUpdateAnime,
  onSendSingleLineNotify,
  isSendingLine,
}) => {
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [episodes, setEpisodes] = useState<AnimeEpisode[]>([]);
  const [synopsis, setSynopsis] = useState('');
  const [wakuUpdatedIso, setWakuUpdatedIso] = useState<string>('');
  const [audioTab, setAudioTab] = useState<'ALL' | 'ซับไทย' | 'พากย์ไทย'>('ALL');
  const [siteTab, setSiteTab] = useState('');
  const [showMalPicker, setShowMalPicker] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [downloadEp, setDownloadEp] = useState<AnimeEpisode | null>(null);
  const [offlineRefresh, setOfflineRefresh] = useState(0);
  useBodyScrollLock(Boolean(anime));

  useEffect(() => {
    if (!anime) return;
    setEpisodes(anime.episodes || []);
    setSynopsis(anime.synopsis || '');
    setWakuUpdatedIso('');
    setShowDownload(false);

    const detailSources = anime.sources.filter(
      (s) =>
        s.url.includes('anime-waku.com') ||
        s.url.includes('animeruka.com') ||
        s.url.includes('bilibili.tv') ||
        s.url.includes('wetv.vip') ||
        s.url.includes('youtube.com/playlist')
    );

    // Instant open: skip re-scraping when saved episodes already cover every audio track we link to
    const savedAudios = new Set((anime.episodes || []).map((e) => e.audio));
    const linkedAudios = new Set(detailSources.map((s) => getSourceAudio(s)));
    const savedCoversAll = [...linkedAudios].every((a) => savedAudios.has(a));
    if (anime.episodes && anime.episodes.length > 0 && savedCoversAll) {
      setIsLoadingDetail(false);
      return;
    }
    if (detailSources.length === 0) return;

    // Fetch every source (sub page + dub page etc.) and merge their episode lists
    setIsLoadingDetail(true);
    Promise.all(detailSources.map((s) => fetchWakuDetail(s.url).catch(() => null)))
      .then((results) => {
        // Ignore source pages that belong to a different show
        const details = results.filter(
          (d): d is NonNullable<typeof d> => !!d && isDetailForAnime(anime, d)
        );
        if (details.length === 0) return;

        const merged = mergeEpisodeLists(anime.episodes || [], ...details.map((d) => d.episodes || []));
        const detail = details.reduce((best, d) => (d.latestEp > best.latestEp ? d : best));
        const synopsisText = details.find((d) => d.synopsis)?.synopsis || '';
        const updatedIso = details.find((d) => d.wakuUpdatedIso)?.wakuUpdatedIso || '';

        if (merged.length > 0) setEpisodes(merged);
        if (synopsisText) setSynopsis(synopsisText);
        if (updatedIso) setWakuUpdatedIso(updatedIso);

        // Update anime if latestEp increased or more episodes loaded
        if (detail.latestEp > anime.latestEp || merged.length > (anime.episodes?.length || 0)) {
          const parsedTime = parseWakuIsoToDayTime(updatedIso);
          onUpdateAnime({
            ...anime,
            latestEp: Math.max(anime.latestEp, detail.latestEp),
            totalEp: anime.totalEp || detail.totalEpisodes,
            synopsis: anime.synopsis || synopsisText,
            episodes: merged.filter((ep) => episodeHasSource(ep, anime.sources)),
            audioType: audioTypeFromSources(anime.sources, anime.audioType),
            broadcastDay:
              anime.broadcastDay === 'UNKNOWN' && detail.broadcastDay && detail.broadcastDay !== 'UNKNOWN'
                ? detail.broadcastDay
                : anime.broadcastDay,
            broadcastTime:
              !anime.broadcastTime && detail.broadcastTime ? detail.broadcastTime : anime.broadcastTime,
            detectedDayNote: parsedTime
              ? `อัปเดตล่าสุด: ${parsedTime.dayNameTh} (${parsedTime.dateStrTh}) เวลา ${parsedTime.timeStr} น.`
              : anime.detectedDayNote,
          });
        }
      })
      .finally(() => setIsLoadingDetail(false));
  }, [anime?.id]);

  if (!anime) return null;

  const seasonCfg = SEASON_CONFIGS[anime.season] || SEASON_CONFIGS.FALL;
  const tierCfg = TIER_CONFIGS[anime.grade];
  const dayCfg = getDayConfig(anime.broadcastDay);

  // Only episodes of websites still linked from this anime (a removed link takes its episodes along)
  const linkedEpisodes = anime ? episodes.filter((ep) => episodeHasSource(ep, anime.sources)) : [];
  // One tab per website, so the same episode from two sites doesn't show up twice in a row
  const SITE_ORDER = ['AnimeRuka', 'Anime-Waku', 'Bilibili', 'WeTV'];
  const siteOf = (ep: AnimeEpisode) => detectPlatformFromUrl(ep.url).name;
  const sites = [...new Set(linkedEpisodes.map(siteOf))].sort((a, b) => {
    const ia = SITE_ORDER.indexOf(a);
    const ib = SITE_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });
  const activeSite = sites.includes(siteTab) ? siteTab : sites[0] || '';
  const siteEpisodes = linkedEpisodes.filter((ep) => siteOf(ep) === activeSite);
  const filteredEpisodes =
    audioTab === 'ALL'
      ? siteEpisodes
      : siteEpisodes.filter((ep) => ep.audio === audioTab);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2.5 sm:p-4 safe-area-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-slate-700/90 rounded-2xl sm:rounded-3xl max-w-3xl w-full max-h-[90vh] supports-[height:100dvh]:max-h-[90dvh] flex flex-col shadow-2xl overflow-hidden"
      >
        {/* Top Hero Banner */}
        <div className="relative bg-gradient-to-br from-slate-950 via-purple-950/40 to-slate-900 p-4 sm:p-6 border-b border-slate-800 shrink-0 space-y-4">
          <button
            type="button"
            onClick={onClose}
            title="ปิด"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 text-slate-400 hover:text-white rounded-xl bg-slate-800/80 hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex gap-3.5 sm:gap-5 items-start">
            <img
              src={formatImageUrl(anime.imageUrl)}
              alt={anime.title}
              className="w-[88px] sm:w-32 aspect-[3/4] object-cover rounded-xl border border-white/15 shadow-xl shrink-0"
            />

            <div className="flex-1 min-w-0 space-y-2 pr-9">
              {/* Badges: one size, one row where it fits */}
              <div className="flex flex-wrap items-center gap-1">
                <span
                  className={`h-6 px-2 inline-flex items-center rounded-md text-[11px] font-extrabold bg-gradient-to-r ${tierCfg.gradient} text-white`}
                >
                  {tierCfg.grade}
                </span>
                <span
                  className={`h-6 px-2 inline-flex items-center rounded-md text-[11px] font-bold border ${seasonCfg.badgeClass}`}
                >
                  {seasonCfg.icon} {seasonCfg.nameEn.split(' ')[0]} {anime.year}
                </span>
                <span
                  className={`h-6 px-2 inline-flex items-center gap-1 rounded-md text-[11px] font-bold border ${dayCfg.badgeBg}`}
                >
                  <Calendar className="w-3 h-3" />
                  {anime.broadcastDay === 'UNKNOWN'
                    ? 'ยังไม่รู้วันฉาย'
                    : `${dayCfg.shortTh} ${anime.broadcastTime || ''}`.trim()}
                </span>
                <MalScoreBadge
                  title={anime.malTitle || anime.title}
                  year={anime.malTitle ? 0 : anime.year}
                  fallback={anime.score}
                  className="h-6 px-2 rounded-md text-[11px]"
                />
              </div>

              <div>
                <h3 className="text-base sm:text-xl font-extrabold text-white leading-snug line-clamp-2">
                  {anime.title}
                </h3>
                {anime.thaiTitle && (
                  <p className="text-xs sm:text-sm text-purple-200/90 font-medium line-clamp-1 mt-0.5">
                    {anime.thaiTitle}
                  </p>
                )}
              </div>

              {/* Episode progress */}
              {(() => {
                const total = anime.totalEp || anime.latestEp || 0;
                const watchedPct = total > 0 ? Math.min(100, (anime.watchedEp / total) * 100) : 0;
                const airedPct = total > 0 ? Math.min(100, (anime.latestEp / total) * 100) : 0;
                return (
                  <div className="space-y-1">
                    <div className="flex items-baseline justify-between gap-2 text-[11px] sm:text-xs">
                      <span className="text-slate-300">
                        ดูแล้ว <strong className="text-amber-300 text-sm">{anime.watchedEp}</strong>
                        <span className="text-slate-500"> / {anime.latestEp} ตอน</span>
                      </span>
                      {anime.totalEp ? <span className="text-slate-500">ทั้งหมด {anime.totalEp}</span> : null}
                    </div>
                    <div className="relative h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-slate-600" style={{ width: `${airedPct}%` }} />
                      <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-orange-500"
                        style={{ width: `${watchedPct}%` }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Actions: same size, same shape; colour only on the icon / active state */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(() => {
              const base =
                'h-10 px-2 rounded-xl border text-xs font-bold flex items-center justify-center gap-1.5 transition-colors active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none';
              const neutral = 'bg-white/[0.04] hover:bg-white/[0.09] border-white/10 text-slate-200';
              return (
                <>
                  <button
                    type="button"
                    onClick={() => onUpdateAnime({ ...anime, notifyLine: !anime.notifyLine })}
                    title="เปิด/ปิดแจ้งเตือน LINE ของเรื่องนี้"
                    className={`${base} ${
                      anime.notifyLine
                        ? 'bg-[#06C755]/15 hover:bg-[#06C755]/25 border-[#06C755]/50 text-[#4ef08f]'
                        : neutral
                    }`}
                  >
                    {anime.notifyLine ? (
                      <Bell className="w-4 h-4 fill-current" />
                    ) : (
                      <BellOff className="w-4 h-4 text-slate-400" />
                    )}
                    <span>{anime.notifyLine ? 'แจ้งเตือน: เปิด' : 'แจ้งเตือน: ปิด'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => onSendSingleLineNotify(anime)}
                    disabled={isSendingLine}
                    className={`${base} ${neutral}`}
                  >
                    {isSendingLine ? (
                      <Loader2 className="w-4 h-4 animate-spin text-[#4ef08f]" />
                    ) : (
                      <Send className="w-4 h-4 text-[#4ef08f]" />
                    )}
                    <span>ส่งเข้า LINE</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowMalPicker((v) => !v)}
                    className={`${base} ${
                      showMalPicker ? 'bg-sky-500/15 border-sky-400/50 text-sky-100' : neutral
                    }`}
                  >
                    <Star className="w-4 h-4 text-amber-300 fill-amber-300" />
                    <span>เรตติ้ง MAL</span>
                  </button>

                  <button
                    type="button"
                    disabled={episodes.length === 0}
                    onClick={() => {
                      setDownloadEp(null);
                      setShowDownload(true);
                    }}
                    className={`${base} bg-teal-500/15 hover:bg-teal-500/25 border-teal-400/50 text-teal-100`}
                  >
                    <Download className="w-4 h-4 text-teal-300" />
                    <span>ดาวน์โหลดตอน</span>
                  </button>
                </>
              );
            })()}
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="p-4 sm:p-6 space-y-5 flex-1 overflow-y-auto overscroll-contain">
          {showMalPicker && (
            <MalRatingPicker
              anime={anime}
              onUpdateAnime={onUpdateAnime}
              onClose={() => setShowMalPicker(false)}
            />
          )}

          {/* Watch Sources */}
          <div className="space-y-2">
            <h4 className="text-xs sm:text-sm font-bold text-slate-300">
              🔗 ช่องทางรับชมหลัก
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {anime.sources
                .filter(
                  (src) =>
                    src.type !== 'netflix' &&
                    !src.name.toLowerCase().includes('netflix') &&
                    !(src.url && src.url.toLowerCase().includes('netflix'))
                )
                .map((src, idx) => {
                const pStyle = getPlatformBadgeStyle(src.type);
                return (
                  <a
                    key={idx}
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`h-11 px-3.5 rounded-xl text-xs sm:text-sm font-bold border flex items-center gap-2 transition-colors shadow ${pStyle.bg}`}
                  >
                    <span>{pStyle.icon}</span>
                    <span className="flex-1 min-w-0 truncate">ดูบน {src.name}</span>
                    <span className="px-1.5 py-0.5 rounded bg-black/30 text-[11px] shrink-0">
                      ตอนล่าสุด {src.latestEp || anime.latestEp}
                    </span>
                    <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                  </a>
                );
              })}
            </div>

            {/* Real Upload Timestamp extracted from anime-waku.com HTML meta tag */}
            {(() => {
              const parsedTime = parseWakuIsoToDayTime(wakuUpdatedIso);
              if (!parsedTime) return null;
              return (
                <div className="mt-2 bg-teal-950/50 border border-teal-500/40 rounded-xl p-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-teal-200 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-teal-400 shrink-0" />
                    <span>
                      🕒 <strong>ข้อมูลเวลาอัปเดตจริงจากโค้ดเว็บ Anime-Waku:</strong> แอดมินเพิ่งลงตอนล่าสุดเมื่อ{' '}
                      <strong className="text-amber-300">
                        {parsedTime.dayNameTh} ({parsedTime.dateStrTh}) เวลา {parsedTime.timeStr} น.
                      </strong>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      onUpdateAnime({
                        ...anime,
                        broadcastDay: parsedTime.dayId,
                        broadcastTime: parsedTime.timeStr,
                        detectedDayNote: `เว็บอัปเดตจริงเมื่อ ${parsedTime.dayNameTh} เวลา ${parsedTime.timeStr} น.`,
                      })
                    }
                    className="px-2.5 py-1 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-[11px] font-bold shrink-0"
                  >
                    ⚡ ตั้งวัน-เวลาฉายตามนี้ ({parsedTime.dayNameTh} {parsedTime.timeStr})
                  </button>
                </div>
              );
            })()}
          </div>

          {/* Synopsis */}
          {synopsis && (
            <div className="bg-slate-950/70 border border-slate-800/90 rounded-2xl p-3.5 space-y-1">
              <h4 className="text-xs font-bold text-purple-300">📖 เรื่องย่อ</h4>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                {synopsis}
              </p>
            </div>
          )}

          {/* Episodes saved in the app (IndexedDB), played by the in-app player */}
          <div className="space-y-2">
            <h4 className="text-xs sm:text-sm font-bold text-emerald-300 flex items-center gap-1.5">
              <Download className="w-4 h-4" />
              <span>ตอนที่โหลดไว้ในแอป</span>
            </h4>
            <OfflineEpisodeList
              animeId={anime.id}
              refreshKey={offlineRefresh}
              onWatched={(ep) => {
                if (ep.number > anime.watchedEp) onUpdateAnime({ ...anime, watchedEp: ep.number });
              }}
            />
          </div>

          {/* Live Episode List from Anime-Waku / Bilibili / WeTV */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-xs sm:text-sm font-bold text-teal-300 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" />
                <span>รายการตอนล่าสุด ({siteEpisodes.length} ตอน)</span>
              </h4>

              {siteEpisodes.length > 0 && (
                <div className="flex items-center bg-slate-950 p-0.5 rounded-xl border border-slate-800">
                  {(['ALL', 'ซับไทย', 'พากย์ไทย'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setAudioTab(tab)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                        audioTab === tab
                          ? 'bg-teal-600 text-white'
                          : 'text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      {tab === 'ALL' ? 'ทั้งหมด' : tab}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {sites.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {sites.map((site) => {
                  const count = linkedEpisodes.filter((ep) => siteOf(ep) === site).length;
                  return (
                    <button
                      key={site}
                      type="button"
                      onClick={() => setSiteTab(site)}
                      className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${
                        activeSite === site
                          ? 'bg-teal-600 border-teal-400 text-white'
                          : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      {site} <span className="font-medium opacity-75">({count})</span>
                    </button>
                  );
                })}
              </div>
            )}

            {isLoadingDetail ? (
              <div className="py-10 flex items-center justify-center gap-2 text-slate-400 text-xs sm:text-sm">
                <Loader2 className="w-5 h-5 text-teal-400 animate-spin" />
                <span>กำลังดึงรายชื่อตอนล่าสุดจากเว็บต้นทาง...</span>
              </div>
            ) : filteredEpisodes.length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-xs bg-slate-950/50 rounded-xl border border-slate-800">
                คลิกปุ่มช่องทางรับชมด้านบนเพื่อเปิดดูอนิเมะเรื่องนี้ได้เลย
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {filteredEpisodes.map((ep, idx) => {
                  const isWatched = anime.watchedEp >= ep.number;
                  return (
                    <div
                      key={`${ep.url}_${idx}`}
                      className={`cv-auto-row p-2.5 rounded-xl border flex items-center justify-between gap-2 transition-colors ${
                        isWatched
                          ? 'bg-emerald-950/20 border-emerald-500/30'
                          : 'bg-slate-950/80 border-slate-800 hover:border-teal-500/50'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 text-[11px] font-bold">
                            ตอนที่ {ep.number}
                          </span>
                          <span
                            className={`px-1.5 py-0.2 rounded text-[10px] font-bold ${
                              ep.audio === 'พากย์ไทย'
                                ? 'bg-sky-500/20 text-sky-300'
                                : 'bg-purple-500/20 text-purple-300'
                            }`}
                          >
                            {ep.audio}
                          </span>
                          {ep.vipBadge && (
                            <span className="px-1.5 py-0.2 rounded bg-amber-500/25 text-amber-300 border border-amber-400/40 text-[10px] font-extrabold">
                              👑 {ep.vipBadge}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-300 truncate mt-1">
                          {ep.title}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() =>
                            onUpdateAnime({ ...anime, watchedEp: ep.number })
                          }
                          title="มาร์คว่าดูถึงตอนนี้แล้ว"
                          className={`px-2 py-1.5 rounded-lg text-[10px] font-bold border flex items-center gap-1 ${
                            isWatched
                              ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                              : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700'
                          }`}
                        >
                          <Check className="w-3 h-3" />
                          <span>{isWatched ? 'ดูแล้ว' : 'มาร์คดู'}</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setDownloadEp(ep);
                            setShowDownload(true);
                          }}
                          title="ดาวน์โหลดตอนนี้"
                          className="px-2 py-1.5 rounded-lg text-[10px] font-bold border bg-teal-600/20 hover:bg-teal-600/40 text-teal-200 border-teal-500/50 flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" />
                          <span>โหลด</span>
                        </button>

                        <a
                          href={ep.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            if (ep.number > anime.watchedEp) {
                              onUpdateAnime({ ...anime, watchedEp: ep.number });
                            }
                          }}
                          className="px-2.5 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow"
                        >
                          <Play className="w-3 h-3 fill-current" />
                          <span>ดูตอนนี้</span>
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-900 px-5 py-3 border-t border-slate-800 flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={() => {
              onClose();
              onEdit(anime);
            }}
            className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-semibold border border-slate-700 flex items-center gap-1.5"
          >
            <Pencil className="w-3.5 h-3.5" />
            <span>แก้ไขข้อมูล / วันฉาย</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold"
          >
            ปิดหน้าต่าง
          </button>
        </div>

        {showDownload && (
          <AnimeDownloadModal
            key={downloadEp ? `${downloadEp.audio}_${downloadEp.number}_${downloadEp.url}` : 'all'}
            anime={anime}
            episodes={linkedEpisodes}
            initialEpisode={
              downloadEp ? { number: downloadEp.number, audio: downloadEp.audio, url: downloadEp.url } : undefined
            }
            onClose={() => {
              setShowDownload(false);
              setOfflineRefresh((n) => n + 1);
            }}
            onSavedToApp={() => setOfflineRefresh((n) => n + 1)}
            onWatched={(ep) => {
              if (ep.number > anime.watchedEp) onUpdateAnime({ ...anime, watchedEp: ep.number });
            }}
          />
        )}
      </div>
    </div>
  );
};
